import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useU2000AuthContext } from "../context/auth/U2000UseAuthContext";
import {
  isMenuPathAllowed,
  resolveU2000DefaultRoute,
  U2000_NO_ALLOWED_SCREEN_PATH,
} from "../context/auth/U2000AuthUtils";
import { useU2000FavoriteMenuHandlers } from "./useU2000FavoriteMenuHandlers";

interface UseU2000DefaultRoutingProps {
  luName?: string;
  fallbackPath?: string;
}

/**
 * Hook pour gérer la redirection vers l'écran par défaut
 * Logique de fallback (BL-0332) via `resolveU2000DefaultRoute` :
 * 1. Écran par défaut des favoris (si défini et encore autorisé dans le menu filtré)
 * 2. Chemin de fallback spécifié (défaut: "/U3400") si tag U3400:READ
 * 3. Premier écran accessible du menu filtré par securityTags
 * 4. /U2106/profile si U2106:READ, sinon /unauthorized
 */
export const useU2000DefaultRouting = ({ luName = "mainMenu", fallbackPath = "/U3400" }: UseU2000DefaultRoutingProps = {}) => {
  const navigate = useNavigate();
  const { auth } = useU2000AuthContext();
  const favoriteHandlers = useU2000FavoriteMenuHandlers({ luName });

  const getDefaultRoute = useCallback((): string => {
    return resolveU2000DefaultRoute({
      menu: auth.menu || [],
      securityTags: auth.securityTags || [],
      defaultScreen: favoriteHandlers.getDefaultScreen(),
      fallbackPath,
    });
  }, [favoriteHandlers, fallbackPath, auth.menu, auth.securityTags]);

  const navigateToDefault = useCallback(() => {
    const defaultRoute = getDefaultRoute();
    if (defaultRoute === U2000_NO_ALLOWED_SCREEN_PATH) {
      navigate(defaultRoute, { replace: true, state: { reason: "no-allowed-screen" } });
      return;
    }
    navigate(defaultRoute, { replace: true });
  }, [getDefaultRoute, navigate]);

  const validateDefaultScreen = useCallback((): boolean => {
    const defaultScreen = favoriteHandlers.getDefaultScreen();
    if (!defaultScreen) return true;

    const favoriteItems = favoriteHandlers.getFavoriteItems();
    const defaultItem = favoriteItems.find((item) => item.path === defaultScreen);
    const allowed = isMenuPathAllowed(auth.menu || [], defaultScreen);

    if (!defaultItem || !allowed) {
      favoriteHandlers.clearDefaultScreen();
      return false;
    }

    return true;
  }, [favoriteHandlers, auth.menu]);

  return {
    getDefaultRoute,
    navigateToDefault,
    validateDefaultScreen,
    currentDefaultScreen: favoriteHandlers.getDefaultScreen(),
  };
};

export default useU2000DefaultRouting;
